'use client';

import { Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import { InverseButton } from '@parkchirp/ui-library';
import { Dispatch, FC, SetStateAction, useCallback } from 'react';

import { GET_SUBLOTS } from '../../../queries';
import { FacilityV2, FeesForRates, RateV2 } from '../../../types';
import { FacilityAutocomplete } from '../../inputs';

interface Props {
	setTabValue: Dispatch<SetStateAction<string>>;
	missingFields: string;
	setMissingFields: Dispatch<SetStateAction<string>>;
	facility: FacilityV2;
	setFacility: Dispatch<SetStateAction<FacilityV2>>;
	clearModal: () => void;
	selectedRate: RateV2 | null;
	setActiveStep: Dispatch<SetStateAction<number>>;
	loadingBillingProfiles: boolean;
	feeOptions: FeesForRates[];
	selectedSublotId: number | null;
	setSelectedSublotId: Dispatch<SetStateAction<number | null>>;
	theme: any;
	isDefault?: boolean;
	hasBillingProfiles: boolean;
}

const FacilityTab: FC<Props> = ({
	setTabValue,
	missingFields,
	setMissingFields,
	facility,
	setFacility,
	clearModal,
	selectedRate,
	setActiveStep,
	loadingBillingProfiles,
	feeOptions,
	selectedSublotId,
	setSelectedSublotId,
	theme,
	isDefault = false,
	hasBillingProfiles,
}) => {
	const handleNext = () => {
		let errorMessage = '';
		if (!facility) {
			errorMessage += 'facility,';
		} else if (!selectedSublotId) {
			errorMessage += 'sublot,';
		}
		if (errorMessage) {
			setMissingFields(errorMessage);
		} else {
			setMissingFields('');
			setActiveStep(1);
			setTabValue('2');
		}
	};

	const handleFacilityChange = useCallback(
		(selectedFacility) => {
			if (selectedFacility?.id) {
				setFacility(selectedFacility);
			}
		},
		[setFacility],
	);

	const { data: sublotsData } = useQuery(GET_SUBLOTS, {
		context: {
			apiVersion: 'v2',
		},
		variables: {
			facilityId: facility ? facility.id : null,
			includePrimary: true,
		},
		skip: !facility,
		fetchPolicy: 'network-only',
	});

	return (
		<div>
			<FacilityAutocomplete
				required
				disabled={!!selectedRate}
				error={missingFields.includes('facility')}
				facilityId={facility?.id ?? ''}
				facilityName={facility?.facilityName ?? ''}
				facilityNumber={String(facility?.facilityNumber) ?? ''}
				onChange={handleFacilityChange}
				disableClearable
				usesDefaultRate={isDefault}
			/>

			{facility && sublotsData && (
				<Grid item xs={6}>
					<FormControl style={{ marginTop: '10px' }} fullWidth variant='outlined'>
						<InputLabel>Default Sublot</InputLabel>
						<Select
							label='Default Sublot'
							value={selectedSublotId || ''}
							error={missingFields.includes('sublot')}
							data-cy='sublot-select'
							onChange={(e) => {
								const subId = Number(e.target.value);
								setSelectedSublotId(subId);
							}}
						>
							{sublotsData?.getSublots?.map((sublot, index) => (
								<MenuItem key={index} value={sublot.id}>
									{sublot.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Grid>
			)}

			<Grid className='pca__ratesForm-containerPadding' spacing={2} container>
				<Grid item xs={6}>
					<InverseButton onClick={clearModal} data-cy='cancel-button' fullWidth name='Cancel' theme={theme} />
				</Grid>
				<Grid item xs={6}>
					<Button
						disabled={
							loadingBillingProfiles ||
							(!isDefault && feeOptions.filter((fee) => fee.feeType.name === 'Monthly').length === 0) ||
							!hasBillingProfiles
						}
						fullWidth
						onClick={handleNext}
						data-cy='next-step-button'
					>
						{loadingBillingProfiles ? (
							<CircularProgress style={{ height: '24px', width: '24px' }} data-cy='loading-indicator' />
						) : (
							<span>Next</span>
						)}
					</Button>
				</Grid>
			</Grid>
		</div>
	);
};

export default FacilityTab;
